import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, inr, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, ArrowsClockwise, Calculator, Cube, Receipt } from "@phosphor-icons/react";

const nowDate = () => new Date().toISOString().slice(0, 10);
const currentMonth = () => new Date().toISOString().slice(0, 7);

export default function Assets() {
    const { user } = useAuth();
    const canEdit = ["founder", "accountant"].includes(user?.role);
    const [assets, setAssets] = useState([]);
    const [summary, setSummary] = useState(null);
    const [categories, setCategories] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newOpen, setNewOpen] = useState(false);
    const [closeOpen, setCloseOpen] = useState(false);
    const [filter, setFilter] = useState({ status: "active", category_id: "" });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [a, s, c, l] = await Promise.all([
                api.get("/assets", { params: {
                    status: filter.status || undefined,
                    category_id: filter.category_id || undefined,
                }}),
                api.get("/assets/summary"),
                api.get("/asset-categories"),
                api.get("/ledgers"),
            ]);
            setAssets(a.data); setSummary(s.data); setCategories(c.data); setLedgers(l.data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [filter.status, filter.category_id]);
    useEffect(() => { load(); }, [load]);

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Fixed Asset Register"
                sub={<>Owned equipment that depreciates over time on the WDV (reducing-balance) method. Depreciation posts to P&amp;L monthly as a non-cash expense.</>}
                right={
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={load} disabled={loading} data-testid="assets-refresh">
                            <ArrowsClockwise size={14} className="mr-1" /> Refresh
                        </Button>
                        {canEdit ? (
                            <>
                                <Button variant="outline" onClick={() => setCloseOpen(true)} data-testid="close-month-btn">
                                    <Calculator size={14} className="mr-2" /> Close month depreciation
                                </Button>
                                <Button onClick={() => setNewOpen(true)} style={{ background: "#0d6efd", color: "#fff" }} data-testid="new-asset-btn">
                                    <Plus size={14} className="mr-2" /> New asset
                                </Button>
                            </>
                        ) : null}
                    </div>
                }
            />

            <div className="p-8 space-y-6">
                {summary ? (
                    <div className="grid gap-4 md:grid-cols-4">
                        <StatTile label="Active assets" value={summary.active_count} isNumber />
                        <StatTile label="Original cost" value={summary.total_original_cost} rupee />
                        <StatTile label="Accumulated depreciation" value={summary.total_accumulated_depreciation} rupee tone="rose" />
                        <StatTile label="Current WDV" value={summary.total_current_wdv} rupee tone="teal" bold />
                    </div>
                ) : null}

                {summary?.by_category && summary.by_category.length > 0 ? (
                    <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                        <div className="px-5 py-3 border-b text-xs uppercase tracking-widest" style={{ borderColor: "var(--se-border)", color: "var(--se-plum)" }}>
                            By category
                        </div>
                        <table className="se-table text-sm" data-testid="assets-by-category">
                            <thead>
                                <tr><th>Category</th><th className="text-right">Assets</th><th className="text-right">Original cost</th><th className="text-right">Current WDV</th></tr>
                            </thead>
                            <tbody>
                                {summary.by_category.map((c) => (
                                    <tr key={c.name} data-testid={`cat-row-${c.name}`}>
                                        <td className="font-medium">{c.name}</td>
                                        <td className="text-right">{c.count}</td>
                                        <td className="se-num text-right">₹ {inr(c.original_cost)}</td>
                                        <td className="se-num text-right">₹ {inr(c.current_wdv)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : null}

                <div className="flex items-center gap-3 px-1">
                    <Label className="text-xs text-stone-500">Filter</Label>
                    <Select value={filter.status || "all"} onValueChange={(v) => setFilter({ ...filter, status: v === "all" ? "" : v })}>
                        <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="sold">Sold</SelectItem>
                            <SelectItem value="disposed">Disposed</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={filter.category_id || "all"} onValueChange={(v) => setFilter({ ...filter, category_id: v === "all" ? "" : v })}>
                        <SelectTrigger className="w-56" data-testid="filter-category"><SelectValue placeholder="All categories" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All categories</SelectItem>
                            {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <div className="ml-auto text-xs text-stone-500">{assets.length} asset{assets.length === 1 ? "" : "s"}</div>
                </div>

                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <table className="se-table text-sm" data-testid="assets-table">
                        <thead>
                            <tr>
                                <th>Asset</th>
                                <th>Category</th>
                                <th>Put to use</th>
                                <th>Location · Dept</th>
                                <th className="text-right">Base cost</th>
                                <th className="text-right">Accum dep</th>
                                <th className="text-right">Current WDV</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {assets.length === 0 ? (
                                <tr><td colSpan="8" className="text-center py-10 text-stone-500">
                                    No assets yet. Click <b>New asset</b> to add one.
                                </td></tr>
                            ) : assets.map((a) => (
                                <tr key={a.id} data-testid={`asset-row-${a.id}`}>
                                    <td>
                                        <Link to={`/assets/${a.id}`} className="font-medium text-blue-700 hover:underline">{a.name}</Link>
                                        {a.description ? <div className="text-[11px] text-stone-500">{a.description}</div> : null}
                                        <div className="text-[10px] text-stone-500 uppercase tracking-wider mt-0.5">
                                            {a.itc_claimed ? "ITC claimed · base=taxable" : "ITC blocked · base=gross"}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="text-sm">{a.category_name}</div>
                                        <div className="text-[10px] text-stone-500">{a.wdv_rate_pct}% p.a. WDV</div>
                                    </td>
                                    <td className="text-xs">{formatDate(a.put_to_use_date)}</td>
                                    <td className="text-xs text-stone-600">
                                        {a.location || "—"}<br/>
                                        <span className="text-[10px] text-stone-500">{a.department || "—"}</span>
                                    </td>
                                    <td className="se-num text-right">₹ {inr(a.opening_wdv)}</td>
                                    <td className="se-num text-right text-rose-700">₹ {inr(a.accumulated_depreciation)}</td>
                                    <td className="se-num text-right font-medium">₹ {inr(a.current_wdv)}</td>
                                    <td>
                                        <StatusPill status={a.status} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <NewAssetDialog open={newOpen} onOpenChange={setNewOpen} categories={categories} ledgers={ledgers} onSaved={load} />
            <CloseMonthDialog open={closeOpen} onOpenChange={setCloseOpen} onDone={load} />
        </>
    );
}

function StatTile({ label, value, rupee, isNumber, tone, bold }) {
    const color = tone === "rose" ? "#dc2626" : tone === "teal" ? "#0d9488" : "#0f172a";
    return (
        <div className="border rounded-md px-5 py-4 bg-white" style={{ borderColor: "var(--se-border)" }} data-testid={`stat-${label}`}>
            <div className="text-[11px] uppercase tracking-widest text-stone-500">{label}</div>
            <div className={`se-num mt-1 ${bold ? "text-3xl font-medium" : "text-2xl"}`} style={{ color }}>
                {rupee ? "₹ " : ""}{isNumber ? Number(value || 0) : inr(value || 0)}
            </div>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        active: { bg: "#dcfce7", fg: "#166534", label: "Active" },
        sold: { bg: "#fef3c7", fg: "#92400e", label: "Sold" },
        disposed: { bg: "#e5e7eb", fg: "#374151", label: "Disposed" },
    };
    const s = map[status] || map.active;
    return <span className="text-[10px] font-semibold rounded-sm px-2 py-0.5" style={{ background: s.bg, color: s.fg }}>{s.label}</span>;
}


function NewAssetDialog({ open, onOpenChange, categories, ledgers, onSaved }) {
    const [f, setF] = useState({
        name: "", description: "", category_id: "",
        department: "", location: "",
        put_to_use_date: nowDate(),
        taxable_value: "", gst_amount: "", gross_invoice_value: "",
        itc_claimed: true,
        wdv_rate_pct: "",
        opening_wdv: "",
        funding_source_ledger_id: "",
        notes: "",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setF({
                name: "", description: "", category_id: "",
                department: "", location: "",
                put_to_use_date: nowDate(),
                taxable_value: "", gst_amount: "", gross_invoice_value: "",
                itc_claimed: true, wdv_rate_pct: "", opening_wdv: "",
                funding_source_ledger_id: "", notes: "",
            });
        }
    }, [open]);

    // Auto-derive gross when taxable + gst change
    useEffect(() => {
        const t = Number(f.taxable_value || 0);
        const g = Number(f.gst_amount || 0);
        if (t > 0 || g > 0) {
            setF((s) => ({ ...s, gross_invoice_value: String((t + g).toFixed(2)) }));
        }
    }, [f.taxable_value, f.gst_amount]);

    // Auto-fill category's default WDV rate
    useEffect(() => {
        if (!f.category_id) return;
        const c = categories.find((x) => x.id === f.category_id);
        if (c && !f.wdv_rate_pct) setF((s) => ({ ...s, wdv_rate_pct: String(c.default_wdv_rate_pct) }));
    }, [f.category_id, categories, f.wdv_rate_pct]);

    const derivedBase = f.itc_claimed ? Number(f.taxable_value || 0) : Number(f.gross_invoice_value || 0);

    const bankOrCashLedgers = (ledgers || []).filter((l) => l.party_type === "bank" || l.party_type === "cash");
    const suspenseLedger = (ledgers || []).find((l) => l.name === "Suspense Account");

    const save = async () => {
        if (!f.name.trim()) return toast.error("Name required");
        if (!f.category_id) return toast.error("Category required");
        if (!f.put_to_use_date) return toast.error("Put-to-use date required");
        if (!(Number(f.taxable_value) > 0)) return toast.error("Taxable value must be > 0");
        setSaving(true);
        try {
            const payload = {
                name: f.name.trim(),
                description: f.description || null,
                category_id: f.category_id,
                department: f.department || null,
                location: f.location || null,
                put_to_use_date: f.put_to_use_date,
                taxable_value: Number(f.taxable_value),
                gst_amount: Number(f.gst_amount || 0),
                gross_invoice_value: Number(f.gross_invoice_value || 0),
                itc_claimed: !!f.itc_claimed,
                wdv_rate_pct: f.wdv_rate_pct ? Number(f.wdv_rate_pct) : null,
                opening_wdv: f.opening_wdv ? Number(f.opening_wdv) : null,
                funding_source_ledger_id: f.funding_source_ledger_id || null,
                notes: f.notes || null,
            };
            await api.post("/assets", payload);
            toast.success("Asset added");
            onOpenChange(false);
            onSaved?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="new-asset-dialog">
                <DialogHeader><DialogTitle className="font-serif text-2xl">Add fixed asset</DialogTitle></DialogHeader>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Asset name *</Label>
                            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. MacBook Pro 14 M4" data-testid="asset-name" />
                        </div>
                        <div>
                            <Label>Category *</Label>
                            <Select value={f.category_id} onValueChange={(v) => setF({ ...f, category_id: v })}>
                                <SelectTrigger data-testid="asset-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                                <SelectContent>
                                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.default_wdv_rate_pct}%</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label>Description</Label>
                        <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Optional" data-testid="asset-description" />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <Label>Put-to-use date *</Label>
                            <Input type="date" value={f.put_to_use_date} onChange={(e) => setF({ ...f, put_to_use_date: e.target.value })} data-testid="asset-put-date" />
                        </div>
                        <div>
                            <Label>Location</Label>
                            <Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="e.g. Main Factory" data-testid="asset-location" />
                        </div>
                        <div>
                            <Label>Department</Label>
                            <Input value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} placeholder="e.g. Ops" data-testid="asset-dept" />
                        </div>
                    </div>

                    <div className="border rounded-sm p-4 space-y-3" style={{ borderColor: "var(--se-border)", background: "#fafaf9" }}>
                        <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-widest text-stone-600 font-medium">Cost + GST</div>
                            <div className="flex items-center gap-2 text-xs">
                                <span>ITC claimed on this asset?</span>
                                <Switch checked={f.itc_claimed} onCheckedChange={(v) => setF({ ...f, itc_claimed: v })} data-testid="asset-itc-toggle" />
                                <span className="font-medium">{f.itc_claimed ? "Yes" : "No (blocked)"}</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <Label>Taxable value (ex-GST) *</Label>
                                <Input type="number" value={f.taxable_value} onChange={(e) => setF({ ...f, taxable_value: e.target.value })} data-testid="asset-taxable" />
                            </div>
                            <div>
                                <Label>GST amount</Label>
                                <Input type="number" value={f.gst_amount} onChange={(e) => setF({ ...f, gst_amount: e.target.value })} data-testid="asset-gst" />
                            </div>
                            <div>
                                <Label>Gross invoice</Label>
                                <Input type="number" value={f.gross_invoice_value} disabled className="bg-stone-100" data-testid="asset-gross" />
                            </div>
                        </div>
                        <div className="text-[11px] text-stone-600 flex items-center gap-2">
                            <Receipt size={12} />
                            <span>
                                Depreciable base = <b>₹{inr(derivedBase)}</b> ·{" "}
                                {f.itc_claimed
                                    ? <>ITC on GST is claimed separately (Input GST). Depreciation runs on the <b>taxable value</b>. (CGST s.16(3) — no double-dip.)</>
                                    : <>ITC is blocked (e.g. cars s.17(5)). GST is capitalised. Depreciation runs on the <b>gross value</b>.</>}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>WDV rate (% p.a.)</Label>
                            <Input type="number" step="0.01" value={f.wdv_rate_pct} onChange={(e) => setF({ ...f, wdv_rate_pct: e.target.value })} data-testid="asset-rate" />
                            <div className="text-[11px] text-stone-500 mt-1">Blank → uses category default.</div>
                        </div>
                        <div>
                            <Label>Opening WDV (for migrated assets)</Label>
                            <Input type="number" value={f.opening_wdv} onChange={(e) => setF({ ...f, opening_wdv: e.target.value })} data-testid="asset-opening-wdv" placeholder={`₹${inr(derivedBase)}`} />
                            <div className="text-[11px] text-stone-500 mt-1">Blank → same as depreciable base (new purchase).</div>
                        </div>
                    </div>

                    <div>
                        <Label>Funding source (bank / cash / suspense)</Label>
                        <Select value={f.funding_source_ledger_id || "none"} onValueChange={(v) => setF({ ...f, funding_source_ledger_id: v === "none" ? "" : v })}>
                            <SelectTrigger data-testid="asset-funding"><SelectValue placeholder="No opening journal (link from purchase later)" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">No opening journal (will link a purchase invoice)</SelectItem>
                                {suspenseLedger ? <SelectItem value={suspenseLedger.id}>{suspenseLedger.name} · migration / opening</SelectItem> : null}
                                {bankOrCashLedgers.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <div className="text-[11px] text-stone-500 mt-1">Posts <b>Dr Fixed Asset — {"<Cat>"} · Cr {"<selected>"}</b> on the put-to-use date.</div>
                    </div>

                    <div>
                        <Label>Notes</Label>
                        <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Optional" data-testid="asset-notes" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={save} disabled={saving} style={{ background: "#0d6efd", color: "#fff" }} data-testid="save-asset">
                        {saving ? "Saving…" : "Add asset"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function CloseMonthDialog({ open, onOpenChange, onDone }) {
    const [month, setMonth] = useState(currentMonth());
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [posting, setPosting] = useState(false);

    const loadPreview = useCallback(async () => {
        if (!open) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/depreciation/preview/${month}`);
            setPreview(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [open, month]);
    useEffect(() => { loadPreview(); }, [loadPreview]);

    const post = async () => {
        setPosting(true);
        try {
            const { data } = await api.post("/depreciation/close-month", { month });
            toast.success(`Posted depreciation for ${month} · ${data.assets_posted} asset(s) · ₹${inr(data.total_depreciation)}`);
            await loadPreview();
            onDone?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setPosting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="close-month-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Close month depreciation</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="flex items-center gap-3">
                        <Label className="text-xs text-stone-500">Month</Label>
                        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                               className="w-48" data-testid="close-month-picker" />
                        <Button variant="outline" onClick={loadPreview} disabled={loading} data-testid="close-preview-reload">
                            <ArrowsClockwise size={14} className="mr-1" /> Reload preview
                        </Button>
                    </div>

                    {preview ? (
                        <>
                            <div className="grid grid-cols-4 gap-3 text-sm">
                                <div className="border rounded-md p-3" style={{ borderColor: "var(--se-border)" }}>
                                    <div className="text-[10px] uppercase text-stone-500">Post date</div>
                                    <div className="font-medium">{preview.post_date}</div>
                                </div>
                                <div className="border rounded-md p-3" style={{ borderColor: "var(--se-border)" }}>
                                    <div className="text-[10px] uppercase text-stone-500">Pending</div>
                                    <div className="font-medium">{preview.assets_pending}</div>
                                </div>
                                <div className="border rounded-md p-3" style={{ borderColor: "var(--se-border)" }}>
                                    <div className="text-[10px] uppercase text-stone-500">Already posted</div>
                                    <div className="font-medium">{preview.assets_already_posted}</div>
                                </div>
                                <div className="border rounded-md p-3" style={{ borderColor: "var(--se-border)", background: "#fef2f2" }}>
                                    <div className="text-[10px] uppercase text-rose-700">Total to post</div>
                                    <div className="se-num text-lg font-medium text-rose-700">₹ {inr(preview.total_depreciation)}</div>
                                </div>
                            </div>

                            <div className="border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                                <table className="se-table text-sm" data-testid="close-preview-table">
                                    <thead>
                                        <tr>
                                            <th>Asset</th>
                                            <th>Category</th>
                                            <th className="text-right">Opening WDV</th>
                                            <th className="text-right">Rate</th>
                                            <th className="text-right">Days</th>
                                            <th className="text-right">Monthly dep</th>
                                            <th className="text-right">WDV after</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.lines.length === 0 ? (
                                            <tr><td colSpan="8" className="text-center py-6 text-stone-500">No active assets applicable for {month}.</td></tr>
                                        ) : preview.lines.map((ln) => (
                                            <tr key={ln.asset_id} data-testid={`preview-row-${ln.asset_id}`}>
                                                <td className="font-medium">{ln.asset_name}</td>
                                                <td className="text-xs text-stone-500">{ln.category}</td>
                                                <td className="se-num text-right">₹ {inr(ln.opening_wdv_this_month || 0)}</td>
                                                <td className="se-num text-right">{ln.annual_rate_pct ? `${ln.annual_rate_pct}%` : "—"}</td>
                                                <td className="text-right text-xs">{ln.days_active || "—"}/{ln.days_in_month || "—"}</td>
                                                <td className="se-num text-right text-rose-700">₹ {inr(ln.monthly_dep || 0)}</td>
                                                <td className="se-num text-right">₹ {inr(ln.wdv_after || 0)}</td>
                                                <td>
                                                    {ln.status === "pending" ? <span className="text-xs text-blue-700">Pending</span>
                                                    : ln.status === "already_posted" ? <span className="text-xs text-emerald-700">Posted</span>
                                                    : <span className="text-xs text-stone-500">{ln.status}</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="text-[11px] text-stone-500 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                                Posts one journal per asset: <b>Dr Depreciation Expense · Cr Accumulated Depreciation — {"<Category>"}</b>, dated {preview.post_date}. Idempotent — running twice for the same month is a no-op. Depreciation is a non-cash expense that lands in <b>Indirect Expenses</b> on the Monthly P&amp;L.
                            </div>
                        </>
                    ) : loading ? <div className="text-sm text-stone-500 py-8 text-center">Computing…</div> : null}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={post} disabled={posting || !preview || preview.assets_pending === 0}
                            style={{ background: "#dc2626", color: "#fff" }} data-testid="close-post-btn">
                        {posting ? "Posting…" : `Post depreciation (${preview?.assets_pending || 0})`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
